import * as React from 'react';
import { Keyboard, ScrollView, StyleSheet, View } from 'react-native';
import Animated, { useAnimatedRef, useSharedValue, useAnimatedScrollHandler, useAnimatedReaction, runOnJS, useDerivedValue, } from 'react-native-reanimated';
import { AppView, NavigationHeader, rem, Spacer, StatusBar } from 'design-system';
import SelectorControllableReanimated from 'design-system/SelectorControllableReanimated';
import useColors from 'hooks/useColors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import InvestActivityTab from '../components/InvestActivityTab';
import { isWeb } from '../../../constants';
const data = [
    {
        title: 'All',
        width: rem(51),
        types: undefined,
        emptyPlaceholder: 'activities',
    },
    {
        title: 'Orders',
        width: rem(81),
        types: ['buy', 'sell', 'switch'],
        emptyPlaceholder: 'orders',
    },
    {
        title: 'Dividends',
        width: rem(103),
        types: ['dividend'],
        emptyPlaceholder: 'dividends',
    },
    {
        title: 'Transactions',
        width: rem(122),
        types: ['withdraw', 'deposit', 'isa_transfer_in'],
        emptyPlaceholder: 'transactions',
    },
    {
        title: 'Interest',
        width: rem(87),
        types: ['interest'],
        emptyPlaceholder: 'interest',
    },
];
const InvestNewActivityScreen = ({ navigation }) => {
    const colors = useColors();
    const { contentWidth, paddingHorizontal } = useAppFrameDimensions(isWeb);
    const scrollX = useSharedValue(0);
    const combinedScrollY = useSharedValue({
        0: 0,
    });
    const currentIndex = useDerivedValue(() => Math.round(scrollX.value / contentWidth), [scrollX, contentWidth]);
    const scrollY = useDerivedValue(() => combinedScrollY.value[currentIndex.value] || 0, [combinedScrollY, currentIndex]);
    const selectorScrollViewRef = React.useRef(null);
    const horizontalScrollViewRef = useAnimatedRef();
    React.useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const scrollXHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollX.value = contentOffset.x;
    }, []);
    const sections = React.useMemo(() => data.map((item) => ({
        ...item,
        id: item.title,
        text: item.title,
        width: item.width,
    })), []);
    const onSelectView = React.useCallback((index) => {
        Keyboard.dismiss();
        horizontalScrollViewRef.current?.scrollTo({
            y: 0,
            animated: true,
            x: index * contentWidth,
        });
    }, [horizontalScrollViewRef, contentWidth]);
    const centerOnItem = React.useCallback((index) => {
        if (selectorScrollViewRef.current && sections[index]) {
            const item = sections[index];
            const itemsX = sections.slice(0, index).reduce((acc, sec) => acc + sec.width + rem(8), 0);
            const itemWidth = item.width;
            const scrollViewWidth = contentWidth - (paddingHorizontal + rem(16)) * 2;
            let scrollToX = itemsX - scrollViewWidth / 2 + itemWidth / 2;
            const totalContentWidth = sections.reduce((acc, sec) => acc + sec.width, 0) + Math.max(0, sections.length - 1) * rem(8);
            const maxScrollX = Math.max(0, totalContentWidth - scrollViewWidth + 32);
            scrollToX = Math.max(0, Math.min(scrollToX, maxScrollX));
            selectorScrollViewRef.current.scrollTo({
                x: scrollToX,
                animated: true,
            });
        }
    }, [sections, contentWidth, paddingHorizontal]);
    useAnimatedReaction(() => Math.round(scrollX.value / contentWidth), (currentPage, previousPage) => {
        if (currentPage !== previousPage) {
            runOnJS(centerOnItem)(currentPage);
        }
    }, [contentWidth]);
    return (<AppView withFrame={false} type="secondary">
      <StatusBar barStyle="dark-content" animated/>
      <NavigationHeader isInTab title={'Activity'} scrollY={scrollY}>
        <Spacer h={8}/>
        <ScrollView horizontal ref={selectorScrollViewRef} showsHorizontalScrollIndicator={false} contentContainerStyle={{
            paddingHorizontal: paddingHorizontal + rem(16),
        }}>
          <SelectorControllableReanimated scrollX={scrollX} sections={sections} screenWidth={contentWidth} setSelection={onSelectView} selectorStyle={styles.selectorStyle} itemContainerStyle={styles.itemContainerStyle}/>
        </ScrollView>
        <Spacer h={8}/>
      </NavigationHeader>
      <Animated.ScrollView horizontal pagingEnabled scrollEnabled decelerationRate="fast" scrollEventThrottle={16} onScroll={scrollXHandler} ref={horizontalScrollViewRef} showsHorizontalScrollIndicator={false} contentContainerStyle={{ width: data.length * contentWidth }}>
        {data.map((eachData, index) => (
        // An explicit width, because flex: 1 pages collapse inside a horizontal ScrollView on web
        <View key={index} style={[styles.item, isWeb && { width: contentWidth }]}>
            <InvestActivityTab index={index} types={eachData.types} combinedScrollY={combinedScrollY} emptyPlaceholder={eachData.emptyPlaceholder}/>
          </View>))}
      </Animated.ScrollView>
    </AppView>);
};
export default InvestNewActivityScreen;
const styles = StyleSheet.create({
    item: {
        flex: 1,
    },
    itemContainerStyle: {
        margin: 0,
        padding: 0,
    },
    selectorStyle: {
        margin: 0,
        padding: 0,
        borderRadius: rem(8),
        justifyContent: 'center',
    },
});
